import { observer, useLocalObservable } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toastError } from '../../components/FormComponents/Toasts'
import Campaign from '../../models/Campaign'
import axiosService from '../../utils/AxiosService'
import CampaignScenario from '../../models/CampaignScenario'
import ProjectAsset from '../../models/ProjectAsset'
import CampaignAuthentication from '../../models/CampaignAuthentication'
import DraftInfluencer from '../../models/DraftInfluencer'
import DraftUpload from '../../models/DraftUpload'
import CampaignReport from '../../models/CampaignReport'
import Influencer from '../../models/Influencer'

export const SharedCampaignContext = React.createContext({})

export default observer(function SharedCampaignProvider(props) {
  const { id } = useParams()
  const navigate = useNavigate()

  const campaignState = useLocalObservable(() =>
    ({
      isLoading: true,
      campaign: new Campaign(),
      campaignReport: new CampaignReport(),
      campaignAuthentications: [],
      influencers: [],
      draftActions: [],
      draftInfluencers: [],
      selectedUpload: new DraftUpload(),
      setSelectedUpload(upload) { this.selectedUpload = upload},
      commentable: false,
      toggleCommentable() { this.commentable = !this.commentable },
      campaignScenarios: [],
      selectedScenario: null,
      campaignTags: [],
      projectAssets: [],
    })
  )

  const getActions = () => {
    if (!campaignState.campaignScenarios.length || !campaignState.selectedScenario?.actions) return
    campaignState.draftActions = campaignState.selectedScenario.actions.filter(action => action.draft_is_visible)
    const infIds = [...new Set(campaignState.draftActions.map(action => action.influencer_id))]
    campaignState.draftInfluencers = infIds.map(id => new DraftInfluencer({ id }))
    setInfluencerActions()
  }

  const actionsForInfluencer = (influencerId) => {
    return campaignState.draftActions.filter(action => +action.influencer_id === +influencerId)
  }

  const setInfluencerActions = () => {
    campaignState.draftInfluencers.forEach(inf => {
      const inf_actions = actionsForInfluencer(inf.id)
      inf.username = inf_actions[0].auth_username
      inf.avatar = inf_actions[0].avatar
      inf.platforms = [... new Set(inf_actions.map(action => action.provider))]
    })
  }

  const getCampaign = () => {
    return axiosService.get(`/api/advertiser/campaign_share/campaigns`)
    .then(campaign => campaignState.campaign = new Campaign(campaign))
    .catch(({ data: { error }}) => {
      toastError(error)
      navigate('/advertiser/share_expired')
    })
  }

  const getAuthentications = () => {
    return axiosService.get(`/api/advertiser/campaign_share/authentications`)
      .then(authentications => {
        campaignState.campaignAuthentications = authentications.map(auth => new CampaignAuthentication(auth))
      })
      .catch(err => console.log(err))
  }

  const getCampaignTags = () => {
    return axiosService.get(`/api/advertiser/campaign_share/campaign_tags`)
    .then(campaignTags => campaignState.campaignTags = campaignTags)
    .catch(err => console.log(err))
  }

  const loadSelectedScenario = (scenarios = campaignState.campaignScenarios) => {
    if (!scenarios.length) {
      campaignState.selectedScenario = null
      return
    }
    const sorted = [...scenarios].sort((a, b) => Number(a.id) - Number(b.id))
    campaignState.selectedScenario = sorted[0] || null
  }

  const loadCampaignScenarios = () => {
    return axiosService.get(`/api/advertiser/campaign_share/campaign_scenarios`)
    .then((campaign_scenarios) => {
      const models = campaign_scenarios.map(cs => new CampaignScenario(cs))
      models.sort((a, b) => Number(a.id) - Number(b.id))
      campaignState.campaignScenarios = models
      loadSelectedScenario(campaignState.campaignScenarios)
    })
    .catch(err => console.log(err))
  }

  const loadInfluencers = () => {
    return axiosService.get(`/api/advertiser/campaign_share/influencers`)
    .then(influencers => campaignState.influencers = influencers.map(influencer => new Influencer(influencer)))
    .catch(err => console.log(err))
  }

  const loadProjectAssets = () => {
    return axiosService.get(`/api/advertiser/campaign_share/project_assets`)
    .then(projectAssets => campaignState.projectAssets = projectAssets.map(projectAsset => new ProjectAsset(projectAsset)))
    .catch(err => console.log(err))
  }

  const loadCampaignReport = () => {
    return axiosService.get(`/api/project_manager/campaigns/${id}/campaign_report`)
    .then(campaignReport => campaignState.campaignReport = new CampaignReport(campaignReport))
    .catch(({ data: { error } }) => toastError(error))
  }

  useEffect(() => {
    campaignState.isLoading = true
    campaignState.campaignScenarios = []
    campaignState.selectedScenario = null
    Promise.all([
      getAuthentications(),
      getCampaign(),
      getCampaignTags(),
      loadInfluencers(),
      loadCampaignScenarios(),
      loadProjectAssets(),
      loadCampaignReport(),
    ]).finally(() => {
      campaignState.isLoading = false
    })
  }, [id])

  useEffect(() => {
    if (campaignState.selectedScenario) {
      getActions()
    }
  }, [campaignState.selectedScenario])

  return (
    <SharedCampaignContext.Provider
      value={{
        campaignState,
        campaign: campaignState.campaign,
        authentications: campaignState.campaignAuthentications,
        campaignReport: campaignState.campaignReport,
        campaignTags: campaignState.campaignTags,
        influencers: campaignState.influencers,
        projectAssets: campaignState.projectAssets,
        campaignScenarios: campaignState.campaignScenarios,
        primaryScenarioId: campaignState.campaignScenarios[0]?.id ?? null,
        selectedScenario: campaignState.selectedScenario,
        draftInfluencers: campaignState.draftInfluencers,
        selectedUpload: campaignState.selectedUpload,
        draftActions: campaignState.draftActions,
        setSelectedUpload: campaignState.setSelectedUpload,
        isLoading: campaignState.isLoading,
        commentable: campaignState.commentable,
        actionsForInfluencer: actionsForInfluencer,
      }}
    >
      {props.children}
    </SharedCampaignContext.Provider>
  )
})
